<script>
  // ── Warengruppen als Filter — dieselben Chips auf beiden Breiten (A7/L6) ──
  //
  // Der Entwurf hat zwei Bedienungen für einen Gedanken: breit eine
  // Anker-Navigation, die zu den Gruppen SPRINGT, schmal Chips, die FILTERN.
  // Gebaut ist nur das Filtern (Plan 1.5, Vorschlag 3).
  //
  // Und das ist kein Verzicht, sondern eine Erleichterung mit einem harten
  // Argument aus dieser Codebasis: Ankersprünge bräuchten ein korrektes
  // `scroll-margin-top` und müssten sich damit in die Klebe-Choreografie der
  // App einreihen (--topbar-h, #backtag-subnav, --bz-sticky-top). Diese
  // Offsets hängen bereits voneinander ab und werden bei `resize` neu
  // gemessen. Ein Filter braucht davon nichts.
  //
  // Der Punkt im Chip ist der einzige Weg, auf dem eine Fehlmenge über die
  // Gruppen hinweg auffällt: Breit sortiert die Liste innerhalb der Gruppe,
  // schmal global — dieselbe Fehlmenge kann schmal Zeile 1 sein und breit
  // Zeile 14 der zweiten Gruppe. In L6 ist er immer aus (keine Prognose), die
  // Anzeige steht aber, sobald L5 sie füllt.

  //
  // RECHTS IN DER LEISTE: WAS DIE ANSICHT HINEINGIBT (`children`)
  // Seit 2026-09-11 der Sortierschalter — er stand vorher in einer eigenen
  // Meta-Zeile, die dafür eine ganze Zeile über der Liste kostete. Die Leiste
  // weiß nicht, was dort steht; die Chips scrollen, der Schluss bleibt stehen.

  /**
   * @type {{ gruppen: any[], gesamt: number, aktiv: string|null,
   *          onwaehlen: (gruppe: string|null) => void,
   *          children?: import('svelte').Snippet }}
   */
  let { gruppen, gesamt, aktiv, onwaehlen, children } = $props();

  const t = (k, p) => window.t(k, p);
</script>

<div class="lg-filter">
<div class="lg-chips" role="group" aria-label={t('lager_filter_label')}>
  <button
    type="button"
    class="lg-chip"
    class:lg-chip--an={aktiv === null}
    aria-pressed={aktiv === null}
    onclick={() => onwaehlen(null)}
  >
    {t('lager_filter_alle')}<b>{gesamt}</b>
  </button>

  {#each gruppen as g (g.warengruppe)}
    <button
      type="button"
      class="lg-chip"
      class:lg-chip--an={aktiv === g.warengruppe}
      aria-pressed={aktiv === g.warengruppe}
      onclick={() => onwaehlen(g.warengruppe)}
    >
      {#if g.fehlt}<i class="lg-chip__punkt" aria-hidden="true"></i>{/if}
      {g.warengruppe}<b>{g.zeilen.length}</b>
    </button>
  {/each}
</div>
{@render children?.()}
</div>

<style>
  /* ── Die Leiste ───────────────────────────────────────────────────────────
     Klebt oben, damit der Filter beim Blättern erreichbar bleibt. `top: 0`
     genügt: Gescrollt wird der body, und die Topbar der App liegt darüber —
     das Panel wird bewusst NICHT zum Scroll-Container gemacht (Plan 14.8).

     Schmal läuft sie von Rand zu Rand wie `.m2-filter` im Entwurf; ab 600 px
     wird sie zur eingerückten Pille wie `.a-nav`. Beides ist gezeichnet, und
     der Unterschied hat einen Grund: Auf 390 px IST der Rand der Bildschirm,
     auf 1000 px liegt die Liste als Karten auf einer Fläche, und eine Leiste
     mit Kanten bis zum Anschlag hätte dort nichts, woran sie anschließt. */
  .lg-filter {
    position: sticky;
    top: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    background: var(--lg-app-glas);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--lg-line);
  }
  /* Nur die Chips scrollen, der Schluss rechts bleibt stehen. Das Polster
     samt Gegenrand hält Platz für den Fokusring frei — der Scroll-Kasten
     schnitte ihn sonst ab. */
  .lg-chips {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    gap: 4px;
    padding: 3px;
    margin: -3px;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .lg-chips::-webkit-scrollbar { display: none; }

  /* ── Die Chips ────────────────────────────────────────────────────────────
     ⚠ KEINE SCHWARZE FLÄCHE FÜR DEN GESETZTEN CHIP (Plan 17.16)
     Das Kit füllt ihn mit --stone-800 (`.m2-chip--on`). In dieser App kommt
     eine gefüllte Fläche in Tiefschwarz sonst NIRGENDS vor: Der aktive Tab ist
     Schrift plus Unterstrich, die Handlungsfarbe ist Terrakotta, und alles
     andere lebt von Rändern auf warmem Grund. Ein schwarzer Block für einen
     FILTER — also für etwas, das nichts auslöst — wäre das lauteste Element
     der Seite.

     Übernommen ist stattdessen das Vokabular von `.a-nav` aus demselben Kit:
     in Ruhe flächenlos, gesetzt eine weiße Karte auf der getönten Leiste. Das
     ist derselbe Gedanke wie überall in der App — der Zustand steht auf dem
     Element, nicht als Farbe darüber. */
  .lg-chip {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 34px;
    padding: 0 12px;
    border: 1px solid transparent;
    border-radius: 999px;
    background: none;
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--lg-ink-mittel);
    white-space: nowrap;
    cursor: pointer;
  }
  .lg-chip:hover { background: var(--lg-card); color: var(--lg-ink); }
  .lg-chip:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }
  .lg-chip b {
    font-weight: 600;
    color: var(--lg-ink-mute);
    font-variant-numeric: tabular-nums;
  }

  /* Gesetzt: eine Karte, die auf der Leiste liegt. Rand und Schatten sind
     dieselben wie bei jeder anderen Karte im Modul. */
  .lg-chip--an {
    background: var(--lg-card);
    border-color: var(--lg-line);
    color: var(--lg-ink);
    font-weight: 700;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
  .lg-chip--an b { color: var(--lg-ink-soft); }
  /* Der gesetzte Chip ist bereits die hellste Fläche — beim Ueberfahren darf
     er nicht die Ruhefarbe der anderen bekommen. `:hover` zaehlt in der
     Spezifitaet wie eine Klasse und schluege `.lg-chip--an` sonst (17.13). */
  .lg-chip.lg-chip--an:hover { background: var(--lg-card); color: var(--lg-ink); }

  .lg-chip__punkt {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--lg-fehlt-edge);
  }

  @container (min-width: 600px) {
    /* Eingerückte Pille statt Leiste von Rand zu Rand (`.a-nav`). */
    .lg-filter {
      margin: 0 20px 4px;
      padding: 6px;
      border-radius: 999px;
      border-bottom: 0;
    }
  }
</style>